import {
  ActivityMetricsV2StoreCoverage,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2StoreSyncRun,
} from '../../../shared/models/activity-metrics-v2.model';

export const ACTIVITY_METRICS_V2_DEFAULT_SOURCES: string[] = [
  'system_log',
  'subject_services',
  'subject_notes',
  'subject_communication_history',
  'files',
  'jobs',
  'subjects',
];

export const ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY: string[] = [
  'service_type',
  'resolution_outcome',
  'resolution_result',
  'resolution_score',
  'state',
  'postcode',
  'suburb_region',
  'service_type_x_resolution_outcome',
  'attempt_lifecycle',
  'subject_lifecycle',
];

export const ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT = 100;

export const ACTIVITY_METRICS_V2_CONTEXT_PANEL_TIMELINE_LIMIT = 100;

export const ACTIVITY_METRICS_V2_ATTEMPTS_INDEXED_GROUP_BY: string[] = [
  'service_type',
  'resolution_outcome',
  'resolution_result',
  'state',
  'attempt_lifecycle',
  'subject_lifecycle',
];

export const ACTIVITY_METRICS_V2_DASHBOARD_INDEXED_GROUP_BY: string[] = [
  'service_type',
  'resolution_outcome',
  'resolution_result',
  'state',
  'suburb_region',
  'attempt_lifecycle',
];

export const ACTIVITY_METRICS_V2_OPERATOR_USER_IDS: number[] = [66];

export interface ActivityMetricsV2CoveragePresentation {
  requestedWindowFromUtc: string | null;
  requestedWindowToUtc: string | null;
  indexedFromUtc: string | null;
  effectiveWindowFromUtc: string | null;
  effectiveWindowToUtc: string | null;
  indexedThroughUtc: string | null;
  gapMs: number | null;
  leadingGapMs: number | null;
  trailingGapMs: number | null;
  isMovingGap: boolean;
  hasIndexedCoverage: boolean;
  isClipped: boolean;
  isClippedStart: boolean;
  isClippedEnd: boolean;
  isFullyIndexed: boolean;
  alertClass: string;
  headline: string;
  detail: string;
  actionLabel: string | null;
  showAction: boolean;
}

export interface ActivityMetricsV2IndexedWindowResolution {
  requestedWindowFromUtc: string | null;
  requestedWindowToUtc: string | null;
  indexedFromUtc: string | null;
  effectiveWindowFromUtc: string | null;
  effectiveWindowToUtc: string | null;
  indexedThroughUtc: string | null;
  gapMs: number | null;
  leadingGapMs: number | null;
  trailingGapMs: number | null;
  isMovingGap: boolean;
  hasIndexedCoverage: boolean;
  isClipped: boolean;
  isClippedStart: boolean;
  isClippedEnd: boolean;
  isFullyIndexed: boolean;
}

export function isActivityMetricsV2Operator(userId: number | string | null | undefined): boolean {
  const numericUserId = Number(userId);
  return Number.isFinite(numericUserId) && ACTIVITY_METRICS_V2_OPERATOR_USER_IDS.includes(numericUserId);
}

export function resolveActivityMetricsV2RequestedSources(
  status: ActivityMetricsV2StoreStatus | null,
  requestedSources: string[] | null | undefined,
): string[] {
  if (requestedSources?.length) {
    return Array.from(new Set(requestedSources.filter((sourceKey) => !!sourceKey)));
  }

  const coverageSources = status?.coverage?.requested_sources ?? [];
  if (coverageSources.length) {
    return Array.from(new Set(coverageSources.filter((sourceKey) => !!sourceKey)));
  }

  const enabledSourceDefinitions = (status?.source_definitions ?? [])
    .filter((source) => source.is_enabled !== false && source.is_enabled !== 0)
    .map((source) => source.source_key)
    .filter((sourceKey) => !!sourceKey);

  if (enabledSourceDefinitions.length) {
    return Array.from(new Set(enabledSourceDefinitions));
  }

  return Array.from(new Set((status?.sync_state ?? []).map((sourceState) => sourceState.source_key).filter((sourceKey) => !!sourceKey)));
}

export function resolveActivityMetricsV2IndexedThroughUtc(
  status: ActivityMetricsV2StoreStatus | null,
  requestedSources: string[] | null | undefined,
): string | null {
  const resolvedSources = resolveActivityMetricsV2RequestedSources(status, requestedSources);
  const requestedSourceSet = new Set(resolvedSources);
  const coverage = status?.coverage;
  const latestIndexedUtc = resolveActivityMetricsV2LatestIndexedUtc(status, requestedSourceSet);
  let indexedThroughUtc: string | null = null;

  if (
    coverage?.coverage_status === 'full'
    && coverage.window_to
    && isCoverageSourceMatch(coverage, requestedSourceSet)
  ) {
    indexedThroughUtc = coverage.window_to;
  }

  if (!indexedThroughUtc) {
    const syncRunIndexedThroughMs = resolveIndexedThroughFromSyncRuns(status?.latest_sync_runs ?? [], requestedSourceSet);
    if (syncRunIndexedThroughMs !== null) {
      indexedThroughUtc = new Date(syncRunIndexedThroughMs).toISOString();
    }
  }

  if (!indexedThroughUtc) {
    const syncStateTimestamps = (status?.sync_state ?? [])
      .filter((sourceState) => requestedSourceSet.size === 0 || requestedSourceSet.has(sourceState.source_key))
      .map((sourceState) => sourceState.last_successful_canonical_timestamp_utc ?? null)
      .filter((timestamp): timestamp is string => !!timestamp)
      .map((timestamp) => Date.parse(timestamp))
      .filter((timestamp) => Number.isFinite(timestamp));

    if (syncStateTimestamps.length) {
      indexedThroughUtc = new Date(Math.min(...syncStateTimestamps)).toISOString();
    }
  }

  if (!indexedThroughUtc) {
    return latestIndexedUtc;
  }

  if (!latestIndexedUtc) {
    return indexedThroughUtc;
  }

  const indexedThroughMs = Date.parse(indexedThroughUtc);
  const latestIndexedMs = Date.parse(latestIndexedUtc);

  if (!Number.isFinite(indexedThroughMs)) {
    return latestIndexedUtc;
  }
  if (!Number.isFinite(latestIndexedMs)) {
    return indexedThroughUtc;
  }

  return new Date(Math.min(indexedThroughMs, latestIndexedMs)).toISOString();
}

export function resolveActivityMetricsV2IndexedFromUtc(
  status: ActivityMetricsV2StoreStatus | null,
  requestedSources: string[] | null | undefined,
): string | null {
  const resolvedSources = resolveActivityMetricsV2RequestedSources(status, requestedSources);
  const requestedSourceSet = new Set(resolvedSources);
  const coverage = status?.coverage;

  if (
    coverage?.coverage_status === 'full'
    && coverage.window_from
    && isCoverageSourceMatch(coverage, requestedSourceSet)
  ) {
    return coverage.window_from;
  }

  const syncRunIndexedFromMs = resolveIndexedFromFromSyncRuns(status?.latest_sync_runs ?? [], requestedSourceSet);

  if (syncRunIndexedFromMs !== null) {
    return new Date(syncRunIndexedFromMs).toISOString();
  }

  return status?.earliest_indexed_utc ?? null;
}

function resolveActivityMetricsV2LatestIndexedUtc(
  status: ActivityMetricsV2StoreStatus | null,
  requestedSourceSet: Set<string>,
): string | null {
  const latestIndexedUtc = status?.latest_indexed_utc ?? null;
  if (!latestIndexedUtc) {
    return null;
  }

  const coverage = status?.coverage ?? null;
  if (!coverage || !requestedSourceSet.size || isCoverageSourceMatch(coverage, requestedSourceSet)) {
    return latestIndexedUtc;
  }

  return null;
}

export function getActivityMetricsV2CoverageGapMs(
  coverage: ActivityMetricsV2StoreCoverage | null | undefined,
  indexedThroughUtc: string | null | undefined,
): number | null {
  if (!coverage?.window_to || !indexedThroughUtc) {
    return null;
  }

  const windowToMs = Date.parse(coverage.window_to);
  const indexedThroughMs = Date.parse(indexedThroughUtc);

  if (!Number.isFinite(windowToMs) || !Number.isFinite(indexedThroughMs) || windowToMs <= indexedThroughMs) {
    return null;
  }

  return windowToMs - indexedThroughMs;
}

export function isActivityMetricsV2MovingCoverageGap(
  coverage: ActivityMetricsV2StoreCoverage | null | undefined,
  indexedThroughUtc: string | null | undefined,
): boolean {
  const gapMs = getActivityMetricsV2CoverageGapMs(coverage, indexedThroughUtc);
  if (!coverage?.window_from || !indexedThroughUtc || !gapMs) {
    return false;
  }

  const windowFromMs = Date.parse(coverage.window_from);
  const indexedThroughMs = Date.parse(indexedThroughUtc);

  if (!Number.isFinite(windowFromMs) || !Number.isFinite(indexedThroughMs)) {
    return false;
  }

  return indexedThroughMs >= windowFromMs;
}

export function buildActivityMetricsV2CoveragePresentation(args: {
  status: ActivityMetricsV2StoreStatus | null;
  requestedSources: string[] | null | undefined;
  requestedWhenFrom?: string | null;
  requestedWhenTo?: string | null;
  formatDate: (value: string) => string;
  formatDuration: (value: number) => string;
}): ActivityMetricsV2CoveragePresentation {
  const { status, requestedSources, requestedWhenFrom, requestedWhenTo, formatDate, formatDuration } = args;
  const coverage = status?.coverage ?? null;
  const resolvedSources = resolveActivityMetricsV2RequestedSources(status, requestedSources);
  const resolution = resolveActivityMetricsV2IndexedWindowResolution({
    status,
    requestedSources: resolvedSources,
    requestedWhenFrom,
    requestedWhenTo,
  });
  const {
    requestedWindowFromUtc,
    requestedWindowToUtc,
    indexedFromUtc,
    effectiveWindowFromUtc,
    effectiveWindowToUtc,
    indexedThroughUtc,
    gapMs,
    leadingGapMs,
    trailingGapMs,
    isMovingGap,
    hasIndexedCoverage,
    isClipped,
    isClippedStart,
    isClippedEnd,
    isFullyIndexed,
  } = resolution;

  if (!coverage && !requestedWindowFromUtc && !requestedWindowToUtc) {
    return {
      requestedWindowFromUtc,
      requestedWindowToUtc,
      indexedFromUtc,
      effectiveWindowFromUtc,
      effectiveWindowToUtc,
      indexedThroughUtc,
      gapMs,
      leadingGapMs,
      trailingGapMs,
      isMovingGap,
      hasIndexedCoverage,
      isClipped,
      isClippedStart,
      isClippedEnd,
      isFullyIndexed,
      alertClass: 'alert alert-info reporting-store-coverage-alert',
      headline: 'Selected window coverage has not been checked yet.',
      detail: 'Refresh reporting-table status to check whether the selected window is indexed.',
      actionLabel: 'Sync current window',
      showAction: false,
    };
  }

  if (!requestedWindowFromUtc || !requestedWindowToUtc) {
    return {
      requestedWindowFromUtc,
      requestedWindowToUtc,
      indexedFromUtc,
      effectiveWindowFromUtc,
      effectiveWindowToUtc,
      indexedThroughUtc,
      gapMs,
      leadingGapMs,
      trailingGapMs,
      isMovingGap,
      hasIndexedCoverage,
      isClipped,
      isClippedStart,
      isClippedEnd,
      isFullyIndexed,
      alertClass: 'alert alert-info reporting-store-coverage-alert',
      headline: 'Selected window is incomplete.',
      detail: 'Choose both a From and To value to resolve the indexed reporting boundary for this request.',
      actionLabel: null,
      showAction: false,
    };
  }

  if (!indexedFromUtc || !indexedThroughUtc) {
    return {
      requestedWindowFromUtc,
      requestedWindowToUtc,
      indexedFromUtc,
      effectiveWindowFromUtc,
      effectiveWindowToUtc,
      indexedThroughUtc,
      gapMs,
      leadingGapMs,
      trailingGapMs,
      isMovingGap,
      hasIndexedCoverage,
      isClipped,
      isClippedStart,
      isClippedEnd,
      isFullyIndexed,
      alertClass: 'alert alert-info reporting-store-coverage-alert',
      headline: 'Indexed results are not available for this source set yet.',
      detail: 'The selected window can stay as-is, but Reporting Tables cannot return indexed results until a trustworthy indexed date span is available.',
      actionLabel: 'Sync current window',
      showAction: true,
    };
  }

  if (!hasIndexedCoverage || !effectiveWindowFromUtc || !effectiveWindowToUtc) {
    const requestedFromMs = requestedWindowFromUtc ? Date.parse(requestedWindowFromUtc) : Number.NaN;
    const requestedToMs = requestedWindowToUtc ? Date.parse(requestedWindowToUtc) : Number.NaN;
    const indexedFromMs = Date.parse(indexedFromUtc);
    const indexedThroughMs = Date.parse(indexedThroughUtc);
    const isEntirelyBeforeIndexedRange = Number.isFinite(requestedToMs) && Number.isFinite(indexedFromMs) && requestedToMs < indexedFromMs;
    const isEntirelyAfterIndexedRange = Number.isFinite(requestedFromMs) && Number.isFinite(indexedThroughMs) && requestedFromMs > indexedThroughMs;

    return {
      requestedWindowFromUtc,
      requestedWindowToUtc,
      indexedFromUtc,
      effectiveWindowFromUtc,
      effectiveWindowToUtc,
      indexedThroughUtc,
      gapMs,
      leadingGapMs,
      trailingGapMs,
      isMovingGap,
      hasIndexedCoverage,
      isClipped,
      isClippedStart,
      isClippedEnd,
      isFullyIndexed,
      alertClass: 'alert alert-info reporting-store-coverage-alert',
      headline: isEntirelyBeforeIndexedRange
        ? 'Selected window is earlier than the current indexed results.'
        : (isEntirelyAfterIndexedRange
          ? 'Selected window is later than the current indexed results.'
          : 'Selected window sits outside the current indexed results.'),
      detail: isEntirelyBeforeIndexedRange
        ? `The selected window ends at ${formatDate(requestedWindowToUtc)}. Indexed results currently begin at ${formatDate(indexedFromUtc)}, so this window cannot return indexed results yet.`
        : (isEntirelyAfterIndexedRange
          ? `The selected window starts at ${formatDate(requestedWindowFromUtc)}. Indexed results currently continue through ${formatDate(indexedThroughUtc)}, so this later window cannot return indexed results yet.`
          : `Indexed results currently cover ${formatDate(indexedFromUtc)} to ${formatDate(indexedThroughUtc)}. This selected window has no overlapping indexed slice yet.`),
      actionLabel: 'Sync current window',
      showAction: true,
    };
  }

  if (isClipped) {
    const leadingGapText = leadingGapMs ? formatDuration(leadingGapMs) : 'an earlier';
    const trailingGapText = trailingGapMs ? formatDuration(trailingGapMs) : 'a later';
    const clippedSpanText = `${formatDate(indexedFromUtc)} through ${formatDate(indexedThroughUtc)}`;
    let headline = 'Showing indexed results for the selected window.';
    let detail = `Selected ${formatDate(requestedWindowFromUtc)} to ${formatDate(requestedWindowToUtc)}. Indexed results currently cover ${formatDate(effectiveWindowFromUtc)} to ${formatDate(effectiveWindowToUtc)} within the available indexed span ${clippedSpanText}. Additional records may appear once the missing date range is indexed.`;

    if (isClippedStart && isClippedEnd) {
      detail = `Selected ${formatDate(requestedWindowFromUtc)} to ${formatDate(requestedWindowToUtc)}. Indexed results currently cover ${formatDate(effectiveWindowFromUtc)} to ${formatDate(effectiveWindowToUtc)}. Records from the earlier ${leadingGapText} and later ${trailingGapText} outside that indexed span may appear once those dates are indexed.`;
    } else if (isClippedStart) {
      detail = `Selected ${formatDate(requestedWindowFromUtc)} to ${formatDate(requestedWindowToUtc)}. Indexed results currently begin at ${formatDate(indexedFromUtc)} and cover ${formatDate(effectiveWindowFromUtc)} to ${formatDate(effectiveWindowToUtc)}. Records from the earlier ${leadingGapText} may appear once that date range is indexed.`;
    } else if (isClippedEnd) {
      detail = `Selected ${formatDate(requestedWindowFromUtc)} to ${formatDate(requestedWindowToUtc)}. Indexed results currently continue through ${formatDate(indexedThroughUtc)} and cover ${formatDate(effectiveWindowFromUtc)} to ${formatDate(effectiveWindowToUtc)}. Records from the later ${trailingGapText} may appear once that date range is indexed.`;
    }

    return {
      requestedWindowFromUtc,
      requestedWindowToUtc,
      indexedFromUtc,
      effectiveWindowFromUtc,
      effectiveWindowToUtc,
      indexedThroughUtc,
      gapMs,
      leadingGapMs,
      trailingGapMs,
      isMovingGap,
      hasIndexedCoverage,
      isClipped,
      isClippedStart,
      isClippedEnd,
      isFullyIndexed,
      alertClass: 'alert alert-info reporting-store-coverage-alert',
      headline,
      detail,
      actionLabel: 'Sync latest gap',
      showAction: true,
    };
  }

  return {
    requestedWindowFromUtc,
    requestedWindowToUtc,
    indexedFromUtc,
    effectiveWindowFromUtc,
    effectiveWindowToUtc,
    indexedThroughUtc,
    gapMs,
    leadingGapMs,
    trailingGapMs,
    isMovingGap,
    hasIndexedCoverage,
    isClipped,
    isClippedStart,
    isClippedEnd,
    isFullyIndexed,
    alertClass: 'alert alert-success reporting-store-coverage-alert',
    headline: 'Requested window is fully indexed and ready for Reporting Tables.',
    detail: `Reporting Tables can run the full requested window from ${formatDate(requestedWindowFromUtc)} to ${formatDate(requestedWindowToUtc)} for ${resolvedSources.length || coverage?.requested_sources.length || 0} selected sources.`,
    actionLabel: null,
    showAction: false,
  };
}

export function resolveActivityMetricsV2IndexedWindowResolution(args: {
  status: ActivityMetricsV2StoreStatus | null;
  requestedSources: string[] | null | undefined;
  requestedWhenFrom?: string | null;
  requestedWhenTo?: string | null;
}): ActivityMetricsV2IndexedWindowResolution {
  const { status, requestedSources, requestedWhenFrom, requestedWhenTo } = args;
  const coverage = status?.coverage ?? null;
  const resolvedSources = resolveActivityMetricsV2RequestedSources(status, requestedSources);
  const indexedFromUtc = resolveActivityMetricsV2IndexedFromUtc(status, resolvedSources);
  const indexedThroughUtc = resolveActivityMetricsV2IndexedThroughUtc(status, resolvedSources);
  const requestedWindowFromUtc = requestedWhenFrom ?? coverage?.window_from ?? null;
  const requestedWindowToUtc = requestedWhenTo ?? coverage?.window_to ?? null;

  const requestedFromMs = requestedWindowFromUtc ? Date.parse(requestedWindowFromUtc) : Number.NaN;
  const requestedToMs = requestedWindowToUtc ? Date.parse(requestedWindowToUtc) : Number.NaN;
  const indexedFromMs = indexedFromUtc ? Date.parse(indexedFromUtc) : Number.NaN;
  const indexedThroughMs = indexedThroughUtc ? Date.parse(indexedThroughUtc) : Number.NaN;
  const effectiveFromMs = Number.isFinite(requestedFromMs) && Number.isFinite(indexedFromMs)
    ? Math.max(requestedFromMs, indexedFromMs)
    : Number.NaN;
  const effectiveToMs = Number.isFinite(requestedToMs) && Number.isFinite(indexedThroughMs)
    ? Math.min(requestedToMs, indexedThroughMs)
    : Number.NaN;

  const hasIndexedCoverage = Number.isFinite(effectiveFromMs)
    && Number.isFinite(effectiveToMs)
    && effectiveFromMs <= effectiveToMs;
  const isClippedStart = hasIndexedCoverage
    && Number.isFinite(requestedFromMs)
    && Number.isFinite(indexedFromMs)
    && indexedFromMs > requestedFromMs;
  const isClippedEnd = hasIndexedCoverage
    && Number.isFinite(requestedToMs)
    && Number.isFinite(indexedThroughMs)
    && indexedThroughMs < requestedToMs;
  const isClipped = isClippedStart || isClippedEnd;
  const isFullyIndexed = hasIndexedCoverage && !isClipped;
  const effectiveWindowFromUtc = hasIndexedCoverage ? new Date(effectiveFromMs).toISOString() : null;
  const effectiveWindowToUtc = hasIndexedCoverage ? new Date(effectiveToMs).toISOString() : null;
  const leadingGapMs = Number.isFinite(requestedFromMs) && Number.isFinite(indexedFromMs) && indexedFromMs > requestedFromMs
    ? indexedFromMs - requestedFromMs
    : null;
  const trailingGapMs = Number.isFinite(requestedToMs) && Number.isFinite(indexedThroughMs) && requestedToMs > indexedThroughMs
    ? requestedToMs - indexedThroughMs
    : null;
  const gapMs = trailingGapMs;
  const isMovingGap = hasIndexedCoverage && trailingGapMs !== null;

  return {
    requestedWindowFromUtc,
    requestedWindowToUtc,
    indexedFromUtc,
    effectiveWindowFromUtc,
    effectiveWindowToUtc,
    indexedThroughUtc,
    gapMs,
    leadingGapMs,
    trailingGapMs,
    isMovingGap,
    hasIndexedCoverage,
    isClipped,
    isClippedStart,
    isClippedEnd,
    isFullyIndexed,
  };
}

export function applyActivityMetricsV2IndexedWindowToRequest<T extends { when_from: string | null; when_to: string | null }>(
  request: T,
  resolution: Pick<ActivityMetricsV2IndexedWindowResolution, 'effectiveWindowFromUtc' | 'effectiveWindowToUtc'>,
): T {
  return {
    ...request,
    when_from: resolution.effectiveWindowFromUtc,
    when_to: resolution.effectiveWindowToUtc,
  };
}

export function getActivityMetricsV2PreviousFullMonthWindow(baseDate?: Date): {
  from: Date;
  to: Date;
  year: number;
  monthIndex: number;
} {
  const now = baseDate ? new Date(baseDate.getTime()) : new Date();
  const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const year = previousMonthDate.getFullYear();
  const monthIndex = previousMonthDate.getMonth();
  const from = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const to = new Date(year, monthIndex + 1, 0, 23, 59, 0, 0);

  return {
    from,
    to,
    year,
    monthIndex,
  };
}

export function buildActivityMetricsV2IndexedBoundaryNotice(args: {
  scopeLabel: string;
  latestKnownBoundaryText: string | null | undefined;
  indexedThroughText: string | null | undefined;
}): string | null {
  const { scopeLabel, latestKnownBoundaryText, indexedThroughText } = args;
  if (!scopeLabel || !latestKnownBoundaryText || !indexedThroughText) {
    return null;
  }

  return `${scopeLabel} continue through ${latestKnownBoundaryText}. Reporting Tables are currently indexed through ${indexedThroughText}, so later items are not yet reported here and the results may change once that date range is indexed.`;
}

function isCoverageSourceMatch(
  coverage: ActivityMetricsV2StoreCoverage,
  requestedSourceSet: Set<string>,
): boolean {
  if (!requestedSourceSet.size) {
    return true;
  }

  const coverageRequestedSet = new Set((coverage.requested_sources ?? []).filter((sourceKey) => !!sourceKey));
  if (!coverageRequestedSet.size) {
    return false;
  }

  if (coverageRequestedSet.size !== requestedSourceSet.size) {
    return false;
  }

  for (const sourceKey of requestedSourceSet.values()) {
    if (!coverageRequestedSet.has(sourceKey)) {
      return false;
    }
  }

  return true;
}

function resolveIndexedThroughFromSyncRuns(
  latestSyncRuns: ActivityMetricsV2StoreSyncRun[],
  requestedSourceSet: Set<string>,
): number | null {
  if (!latestSyncRuns.length || !requestedSourceSet.size) {
    return null;
  }

  const maxWindowToBySource = new Map<string, number>();

  for (const run of latestSyncRuns) {
    if (run.status !== 'complete' || !run.window_to_utc) {
      continue;
    }

    const windowToMs = Date.parse(run.window_to_utc);
    if (!Number.isFinite(windowToMs)) {
      continue;
    }

    const resolvedSources = resolveSyncRunSources(run, requestedSourceSet);
    for (const sourceKey of resolvedSources) {
      const existing = maxWindowToBySource.get(sourceKey);
      if (existing === undefined || windowToMs > existing) {
        maxWindowToBySource.set(sourceKey, windowToMs);
      }
    }
  }

  if (maxWindowToBySource.size !== requestedSourceSet.size) {
    return null;
  }

  return Math.min(...Array.from(maxWindowToBySource.values()));
}

function resolveIndexedFromFromSyncRuns(
  latestSyncRuns: ActivityMetricsV2StoreSyncRun[],
  requestedSourceSet: Set<string>,
): number | null {
  if (!latestSyncRuns.length || !requestedSourceSet.size) {
    return null;
  }

  const minWindowFromBySource = new Map<string, number>();

  for (const run of latestSyncRuns) {
    if (run.status !== 'complete' || !run.window_from_utc) {
      continue;
    }

    const windowFromMs = Date.parse(run.window_from_utc);
    if (!Number.isFinite(windowFromMs)) {
      continue;
    }

    const resolvedSources = resolveSyncRunSources(run, requestedSourceSet);
    for (const sourceKey of resolvedSources) {
      const existing = minWindowFromBySource.get(sourceKey);
      if (existing === undefined || windowFromMs < existing) {
        minWindowFromBySource.set(sourceKey, windowFromMs);
      }
    }
  }

  if (minWindowFromBySource.size !== requestedSourceSet.size) {
    return null;
  }

  return Math.max(...Array.from(minWindowFromBySource.values()));
}

function resolveSyncRunSources(
  run: Pick<ActivityMetricsV2StoreSyncRun, 'source_key' | 'notes'>,
  requestedSourceSet: Set<string>,
): string[] {
  const resolved = new Set<string>();
  const explicitSourceKey = typeof run.source_key === 'string' ? run.source_key.trim() : '';

  if (explicitSourceKey && requestedSourceSet.has(explicitSourceKey)) {
    resolved.add(explicitSourceKey);
  }

  if (!resolved.size) {
    for (const sourceKey of parseSyncRunNoteSources(run.notes, 'requested_sources')) {
      if (requestedSourceSet.has(sourceKey)) {
        resolved.add(sourceKey);
      }
    }
  }

  for (const invalidatedSource of parseSyncRunNoteSources(run.notes, 'invalidated_sources')) {
    resolved.delete(invalidatedSource);
  }

  return Array.from(resolved);
}

function parseSyncRunNoteSources(notes: string | null | undefined, noteKey: string): string[] {
  if (!notes || !noteKey) {
    return [];
  }

  const escapedKey = noteKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`(?:^|\\s\\|\\s)${escapedKey}=([A-Za-z0-9_,.-]+)`);
  const matches = pattern.exec(notes);

  if (!matches?.[1]) {
    return [];
  }

  return Array.from(new Set(
    matches[1]
      .split(',')
      .map((sourceKey) => sourceKey.trim())
      .filter((sourceKey) => !!sourceKey)
  ));
}
